import { useState, useEffect } from 'react'
import { makeStyles } from '@material-ui/styles'
import jwt_decode from 'jwt-decode'
import { useDispatch, useSelector } from 'react-redux'
import { useConfirm } from 'material-ui-confirm'
import { useForm } from 'react-hook-form'
import PropTypes from 'prop-types'

import {
  Grid,
  Button,
  TextField,
  Typography,
  CircularProgress,
  InputAdornment,
  IconButton
} from '@material-ui/core'
import { Visibility, VisibilityOff } from '@material-ui/icons'
import { userRequestOnSignIn } from '../../store/modules/user/actions'

import api from '../../repositories'
import { resetErrorsList } from '../../store/modules/error/action'

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: theme.palette.background.default,
    height: '100%'
  },
  grid: {
    height: '100%'
  },
  quoteContainer: {
    [theme.breakpoints.down('md')]: {
      display: 'none'
    }
  },
  quote: {
    background: `linear-gradient(to bottom right, ${theme.palette.primary.light}, ${theme.palette.primary.dark})`,
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  quoteInner: {
    textAlign: 'center',
    flexBasis: '600px'
  },
  quoteText: {
    color: theme.palette.white,
    fontWeight: 300
  },
  contentContainer: {},
  content: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column'
  },
  contentHeader: {
    display: 'flex',
    alignItems: 'center',
    paddingTop: theme.spacing(5),
    paddingBototm: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2)
  },
  logoImage: {
    marginLeft: theme.spacing(4)
  },
  contentBody: {
    flexGrow: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    [theme.breakpoints.down('md')]: {
      justifyContent: 'center'
    }
  },
  form: {
    width: '50%',
    [theme.breakpoints.down('sm')]: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2)
    }
  },
  title: {
    marginTop: theme.spacing(3)
  },
  socialButtons: {
    marginTop: theme.spacing(3)
  },
  socialIcon: {
    marginRight: theme.spacing(1)
  },
  sugestion: {
    marginTop: theme.spacing(2)
  },
  textField: {
    marginTop: theme.spacing(2)
  },
  signInButton: {
    margin: theme.spacing(2, 0)
  }
}))

export default function SignIn({ history }) {
  const [passwordVisible, setPasswordVisible] = useState(false)
  const [responseError, setResponseError] = useState()

  const { loading } = useSelector(state => state.user)

  const classes = useStyles()
  const confirm = useConfirm()
  const { register, handleSubmit, errors } = useForm()

  const dispatch = useDispatch()

  const handleSignIn = data => {
    api
      .login(data.email, data.password)
      .then(login => {
        if (login) {
          const user = jwt_decode(login)
          window.localStorage.setItem('@NeoPro:token', login)
          dispatch(resetErrorsList())
          dispatch(userRequestOnSignIn(user.id, login))
          setResponseError()
        }
      })
      .catch(err => {
        if (err.response && err.response.status) {
          switch (err.response.status) {
            case 401:
              setResponseError(err.response.data.error.friendlyMsg)
              break
            case 500:
              setResponseError(err.response.data.error.friendlyMsg)
              break
            default:
              confirm({
                title: 'Ops!',
                description: 'Deu algum problema...',
                confirmationText: 'Ok!',
                cancellationText: 'Fechar'
              })
                .then(() => {})
                .catch(() => {})
              break
          }
        } else {
          confirm({
            title: 'Ops!',
            description: !err.isAxiosError
              ? err.message
              : 'Ops, pelo jeito o servidor está desligado.',
            confirmationText: 'Ok!',
            cancellationText: 'Fechar'
          })
            .then(() => {})
            .catch(() => {})
        }
      })
  }

  return (
    <div className={classes.root}>
      <Grid className={classes.grid} container>
        <Grid className={classes.quoteContainer} item lg={7}>
          <div className={classes.quote}>
            <div className={classes.quoteInner}>
              <img src='/images/logo.svg' alt='Logo' height='80px' />
            </div>
          </div>
        </Grid>
        <Grid className={classes.content} item lg={5} sm={12} xl={5} xs={12}>
          <div className={classes.content}>
            <div className={classes.contentBody}>
              <form
                className={classes.form}
                onSubmit={handleSubmit(handleSignIn)}
              >
                <Grid container spacing={1} direction='column'>
                  <Grid item lg={12} sm={12} xl={12} xs={12}>
                    <Typography className={classes.title} variant='h2'>
                      Entrar
                    </Typography>
                  </Grid>
                  <Grid item lg={12} sm={12} xl={12} xs={12}>
                    <TextField
                      className={classes.textField}
                      error={!!errors.email || !!responseError}
                      fullWidth
                      helperText={!!errors.email && 'Login é obrigatório'}
                      label='Email/CPF'
                      name='email'
                      inputRef={register({ required: true })}
                      variant='outlined'
                      inputProps={{
                        'data-cy': 'email',
                        'data-clarity-unmask': 'true'
                      }}
                    />
                  </Grid>
                  <Grid item lg={12} sm={12} xl={12} xs={12}>
                    <TextField
                      className={classes.textField}
                      error={!!errors.password || !!responseError}
                      fullWidth
                      helperText={
                        !(!errors.password && 'Senha é obrigatória') ||
                        (!!responseError && responseError)
                      }
                      InputProps={{
                        'data-clarity-mask': 'True',
                        'data-cy': 'password',
                        endAdornment: (
                          <InputAdornment position='end'>
                            <IconButton
                              aria-label='toggle password visibility'
                              onClick={() =>
                                setPasswordVisible(state => !state)
                              }
                              edge='end'
                            >
                              {passwordVisible ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                      label='Senha'
                      name='password'
                      variant='outlined'
                      type={passwordVisible ? 'text' : 'password'}
                      inputRef={register({ required: true })}
                    />
                  </Grid>
                  <Grid item lg={12} sm={12} xl={12} xs={12}>
                    <Button
                      className={classes.signInButton}
                      color='primary'
                      disabled={loading}
                      fullWidth
                      size='large'
                      type='submit'
                      variant='contained'
                      name='btn-login'
                      data-cy='btn-login'
                    >
                      {loading ? (
                        <CircularProgress
                          size={24}
                          className={classes.buttonProgress}
                        />
                      ) : (
                        'Entrar'
                      )}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </div>
          </div>
        </Grid>
      </Grid>
    </div>
  )
}

SignIn.propTypes = {
  history: PropTypes.any
}
